import LegalPageLayout from '@/components/LegalPageLayout';

export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout
      testId="privacy-policy-page"
      title="Privacy Policy"
      subtitle="How Peak 9 collects, uses, and protects your personal information."
      lastUpdated="August 2026"
    >
      <h2>1. Introduction</h2>
      <p>
        Peak 9 ("Peak 9", "we", "us", or "our") is a performance coaching and leadership development
        practice headquartered in Mumbai, India, with a presence in Bangalore, India and Spain. This
        Privacy Policy explains how we collect, use, disclose, and safeguard your personal information
        when you visit our website, subscribe to our newsletter, submit an enquiry, take our diagnostic
        quiz, or engage with our keynotes, workshops, coaching programs, or the "Performance by Design"
        course.
      </p>
      <p>
        By using our website, you agree to the collection and use of information in accordance with this
        policy. If you do not agree, please discontinue use of the website.
      </p>

      <h2>2. Who We Are (Data Controller)</h2>
      <p>
        <strong>Peak 9</strong><br />
        Mumbai, India<br />
        Contact: <a href="mailto:contact@peak9.in">contact@peak9.in</a>
      </p>
      <p>
        For the purposes of applicable data protection laws (including the EU/UK General Data Protection
        Regulation and India's Digital Personal Data Protection Act, 2023), Peak 9 acts as the "data
        controller" / "data fiduciary" for personal information collected through this website.
      </p>

      <h2>3. Information We Collect</h2>
      <h3>Information you provide to us</h3>
      <ul>
        <li>Name, email address, and phone number (contact, keynote, and workshop enquiry forms)</li>
        <li>Company/organisation name (contact form)</li>
        <li>Email address (newsletter subscription)</li>
        <li>First name and email address, plus your answers to diagnostic questions (Diagnostic Quiz / "The 1% Gap Diagnostic")</li>
        <li>Any information you submit via our embedded Tally.so application or enquiry forms</li>
        <li>Correspondence you send us directly (e.g. by email)</li>
      </ul>
      <h3>Information collected automatically</h3>
      <ul>
        <li>Standard technical data such as IP address, browser type, device type, and pages visited, for security and site performance purposes</li>
        <li>Cookies and similar technologies — see our <a href="/cookie-policy">Cookie Policy</a> for details</li>
      </ul>

      <h2>4. How We Use Your Information</h2>
      <ul>
        <li>To respond to enquiries about keynotes, workshops, coaching, or the course</li>
        <li>To deliver your diagnostic quiz results and related follow-up content</li>
        <li>To send newsletters and updates you have opted in to receive</li>
        <li>To operate, secure, and improve our website</li>
        <li>To comply with legal obligations</li>
      </ul>

      <h2>5. Legal Basis for Processing (EU/UK Users)</h2>
      <p>
        Where GDPR applies, we process your personal data on the basis of: (a) your consent (e.g.
        newsletter sign-up, quiz submission); (b) our legitimate interests in responding to enquiries and
        operating our business; and (c) compliance with legal obligations where applicable.
      </p>

      <h2>6. Cookies</h2>
      <p>
        We currently use only essential cookies required for the website to function. We may introduce
        analytics cookies in the future to help us understand site usage. Full details are available in
        our <a href="/cookie-policy">Cookie Policy</a>.
      </p>

      <h2>7. Third-Party Services</h2>
      <p>We use the following third-party services, which may process your information on our behalf:</p>
      <ul>
        <li><strong>Tally.so</strong> — for embedded application and enquiry forms</li>
        <li><strong>Google Calendar</strong> — for scheduling calls, via an external booking link</li>
        <li>Our website hosting and infrastructure providers</li>
      </ul>
      <p>These providers only receive the information necessary to perform their function and are not permitted to use it for any other purpose.</p>

      <h2>8. Data Sharing & Disclosure</h2>
      <p>
        We do not sell your personal information. We may share it with service providers who support our
        operations (as listed above), or where required by law, regulation, or valid legal process.
      </p>

      <h2>9. International Data Transfers</h2>
      <p>
        As Peak 9 operates across India and Spain, your information may be transferred to and processed in
        countries other than your own. Where required, we take steps to ensure such transfers are subject
        to appropriate safeguards.
      </p>

      <h2>10. Data Retention</h2>
      <p>
        We retain personal information only for as long as necessary to fulfil the purposes described in
        this policy, or as required by law. You may request deletion at any time (see Section 11).
      </p>

      <h2>11. Your Rights</h2>
      <h3>If you are in the EU/UK (GDPR)</h3>
      <p>You have the right to access, correct, erase, restrict, or port your data, and to object to or withdraw consent for processing.</p>
      <h3>If you are in India (DPDP Act, 2023)</h3>
      <p>You have the right to access a summary of your personal data, seek correction or erasure, and withdraw consent, as well as the right to grievance redressal.</p>
      <h3>If you are in California (CCPA/CPRA)</h3>
      <p>You have the right to know what personal information is collected, request deletion, and opt out of the sale/sharing of personal information (note: we do not sell personal information).</p>
      <p>To exercise any of these rights, contact us at <a href="mailto:contact@peak9.in">contact@peak9.in</a>.</p>

      <h2>12. Data Security</h2>
      <p>
        We implement reasonable technical and organisational measures, including access controls and
        security headers, to protect your personal information. However, no method of transmission over
        the internet is 100% secure.
      </p>

      <h2>13. Children's Privacy</h2>
      <p>Our services are intended for individuals aged 18 and above. We do not knowingly collect personal information from children.</p>

      <h2>14. Changes to This Policy</h2>
      <p>We may update this Privacy Policy from time to time. The "Last updated" date at the top reflects the most recent revision.</p>

      <h2>15. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or wish to exercise your data rights, please
        contact us at <a href="mailto:contact@peak9.in">contact@peak9.in</a>.
      </p>
    </LegalPageLayout>
  );
}
